import React, { useState, useEffect } from "react";
import { Check } from "@phosphor-icons/react";
import { useLocale } from "lib/hooks/useLocale";
import { CouponPageConfigType } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "@shopflo/ui/utils";
import { currencyFormatter } from "lib/utils/formatters";
import BrandLogoImage from "components/common/loaders/BrandLogoImage";

interface PartnerOffersCardProps {
  offerId: string;
  title: string;
  description: string;
  logoUrl?: string;
  tncs: string[];
  offerState: string;
  offerCode?: string;
  minimumOrderValue?: number;
  brandLabel?: string;
  onCollect: (offerId: string) => void;
  onRemove: (offerId: string) => void;
  customisations?: CouponPageConfigType;
}

const PartnerOffersCard: React.FC<PartnerOffersCardProps> = React.memo(
  ({ offerId, title, description, logoUrl, tncs, offerState, minimumOrderValue, brandLabel, onCollect, onRemove, customisations }) => {
    const { t } = useLocale();
    const [showMore, setShowMore] = useState<boolean>(false);
    const isCollectedFromServer = offerState === "COLLECTED" || offerState === "ALREADY_APPLIED";
    const [optimisticCollected, setOptimisticCollected] = useState<boolean | null>(null);
    const isCollected = optimisticCollected ?? isCollectedFromServer;

    useEffect(() => {
      setOptimisticCollected(null);
    }, [offerState]);
    const isApplicable = !isCollected && (offerState === "APPLICABLE" || isCollectedFromServer);

    const primaryColor = customisations?.primaryColor ?? "#000000";

    return (
      <div
        className="flex w-full flex-col gap-3 p-3"
        style={{
          borderRadius: customisations?.cardRadius ?? 12,
          border: "1px solid #FFF",
          background: `linear-gradient(180deg, ${hexToRgba(primaryColor, 0.10)} 0%, ${hexToRgba(primaryColor, 0.00)} 50%), #FFF`,
          boxShadow: "0 0 0 1px rgba(10, 13, 18, 0.05), 0 1px 2px 0 rgba(10, 13, 18, 0.05)",
        }}>

        {/* Logo + title + collect */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {logoUrl && (
              <BrandLogoImage
                src={logoUrl}
                alt={title}
                className="aspect-square h-10 w-10 shrink-0 rounded-full border border-black/[0.06] bg-gray-300 object-cover"
              />
            )}
            <div className="flex min-w-0 flex-col gap-0.5">
              {brandLabel && (
              <p className="text-text-xxs-medium" style={{ color: "var(--flo-coal-light-color)" }}>{brandLabel}</p>
            )}
              <p className="text-text-sm-semibold" style={{ color: "var(--flo-coal-dark-color)" }}>{title}</p>
            </div>
          </div>

          {isCollected ? (
            <button
              type="button"
              className="flex shrink-0 items-center justify-center gap-1.5 px-2 hover:[background:color-mix(in_srgb,transparent,#000000_4%)]"
              style={{
                height: 32,
                borderRadius: customisations?.buttonRadius ?? 8,
                border: `1px solid ${hexToRgba(primaryColor, 0.10)}`,
              }}
              onClick={() => { setOptimisticCollected(false); onRemove(offerId); }}>
              <div className="flex items-center justify-center" style={{ width: 16, height: 16, gap: 3.2 }}>
                <Check size={12} weight="bold" color={primaryColor} />
              </div>
              <span className="text-text-xs-bold" style={{ color: primaryColor }}>{t("partner_offers.collected")}</span>
            </button>
          ) : isApplicable ? (
            <button
              type="button"
              className="flex shrink-0 items-center justify-center gap-2 px-3 hover:[background:color-mix(in_srgb,var(--btn-bg),#000000_4%)]"
              style={{
                height: 32,
                borderRadius: customisations?.buttonRadius ?? 8,
                backgroundColor: customisations?.primaryColor,
                ["--btn-bg" as string]: customisations?.primaryColor,
              }}
              onClick={() => { setOptimisticCollected(true); onCollect(offerId); }}>
              <span className="text-text-xs-bold text-white">{t("partner_offers.collect")}</span>
            </button>
          ) : null}
        </div>

        {/* Description + T&Cs */}
        {(description || minimumOrderValue || (tncs && tncs.length > 0)) && (
          <div className="flex flex-col gap-1">
            {description && (
              <p className="text-text-xs-medium" style={{ color: "#000", opacity: 0.5 }}>{description}</p>
            )}
            {minimumOrderValue && (
              <p className="text-text-xs-medium" style={{ color: "#000", opacity: 0.5 }}>
                {`Minimum order value: ${currencyFormatter(minimumOrderValue)}`}
              </p>
            )}
            {tncs?.filter(Boolean).length > 0 && (
              showMore ? (
                <>
                  <ul className="my-2 flex w-full flex-col space-y-1">
                    {tncs.filter(Boolean).map((point: string, index: number) => (
                      <li className="ml-4 list-disc text-xs font-normal text-coal-light" key={index}>
                        <p>{point}</p>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setShowMore(false)}
                    className="w-fit text-text-xxs-medium text-coal-dark outline-none">
                    {t("partner_offers.hide_details")}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMore(true)}
                  className="w-fit text-text-xxs-medium outline-none text-zinc-500">
                  {t("more")}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  },
);

export default PartnerOffersCard;
